import type { LiveGambitDto } from '@/core/data/gambit/gambit.interface';
import { LiveGambitCheckIn } from '@/core/data/gambit/gambit.type';
import type { LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { ActiveCheckIn } from '@/stores/navigation.store';
import { isInGambitCheckInWindow } from '@/views/main/gambits/utils/gambit-check-in-window';
import { subHours } from 'date-fns';

/** An ended gambit stays in the banner for a day, unless it is closed sooner. */
export const RECENT_GAMBIT_HOURS = 24;

export type LiveBannerItem =
  /** Attending, check-in open, not checked in. No X: leaving the gambit is how it goes. */
  | { kind: 'check-in'; id: string; gambit: LiveGambitDto }
  /** Checked into the gambit. No X: checking out is how it goes. */
  | { kind: 'checked-in'; id: string; gambit: LiveGambitDto }
  /** Checked into a spot outside any gambit in the banner. */
  | { kind: 'spot'; id: string; checkIn: ActiveCheckIn }
  | { kind: 'handshake'; id: string; handshake: LiveHandshakeDto }
  /** Ended in the last day: who played. The X takes it off. */
  | { kind: 'recent'; id: string; gambit: LiveGambitDto };

interface LiveBannerSources {
  gambits: LiveGambitDto[];
  spotCheckIn: ActiveCheckIn | null;
  handshakes: LiveHandshakeDto[];
  now: Date;
}

const gambitItem = (gambit: LiveGambitDto, spotCheckIn: ActiveCheckIn | null, now: Date): LiveBannerItem | null => {
  const end = new Date(gambit.end);

  if (end <= now) {
    return end >= subHours(now, RECENT_GAMBIT_HOURS) ? { kind: 'recent', id: `recent-${gambit.id}`, gambit } : null;
  }

  // The spot check-in is the phone's own word, so a check-in just made counts before the reload lands.
  if (gambit.checkIn === LiveGambitCheckIn.ACTIVE || spotCheckIn?.gambitId === gambit.id) {
    return { kind: 'checked-in', id: `gambit-${gambit.id}`, gambit };
  }

  // Checked out before the end: gone until it comes back as recent.
  if (gambit.checkIn === LiveGambitCheckIn.LEFT) return null;

  return isInGambitCheckInWindow(gambit.start, gambit.end, now)
    ? { kind: 'check-in', id: `gambit-${gambit.id}`, gambit }
    : null;
};

const isOpenHandshake = (handshake: LiveHandshakeDto, now: Date): boolean =>
  !handshake.closedAt && new Date(handshake.liveUntil) > now;

/** One row per live thing, in the order they ask for attention: now, then the people, then what just ended. */
export function liveBannerItems({ gambits, spotCheckIn, handshakes, now }: LiveBannerSources): LiveBannerItem[] {
  const gambitItems = gambits
    .map((gambit) => gambitItem(gambit, spotCheckIn, now))
    .filter((item): item is LiveBannerItem => !!item);

  const checkedIn = gambitItems.filter((item) => item.kind === 'checked-in');
  const checkIn = gambitItems.filter((item) => item.kind === 'check-in');
  const recent = gambitItems.filter((item) => item.kind === 'recent');

  const isSpotCovered = checkedIn.some(
    (item) =>
      item.kind === 'checked-in' &&
      (item.gambit.id === spotCheckIn?.gambitId || item.gambit.location?.id === spotCheckIn?.locationId),
  );
  const spot: LiveBannerItem[] =
    spotCheckIn && !isSpotCovered ? [{ kind: 'spot', id: `spot-${spotCheckIn.locationId}`, checkIn: spotCheckIn }] : [];

  const handshakeItems: LiveBannerItem[] = handshakes
    .filter((handshake) => isOpenHandshake(handshake, now))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((handshake) => ({ kind: 'handshake', id: `handshake-${handshake.id}`, handshake }));

  return [...checkedIn, ...checkIn, ...spot, ...handshakeItems, ...recent];
}
